import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../config/authConfig';
import './sidebar.css';

export default function Sidebar({ collapsed, onToggle }) {
  const location = useLocation();
  const { instance, accounts, inProgress } = useMsal();

  const [barTop, setBarTop] = useState(0);
  const [showText, setShowText] = useState(!collapsed);
  const [isAppsMenuOpen, setIsAppsMenuOpen] = useState(false);
  const linkRefs = useRef({});
  const appsMenuRef = useRef(null);

  const isSignedIn = accounts && accounts.length > 0;
  const canShowSignIn = !isSignedIn && inProgress === 'none';
  const displayName = isSignedIn ? accounts[0]?.name || accounts[0]?.username : 'Not signed in';

  const handleSignIn = async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Sidebar sign-in redirect failed:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await instance.logoutRedirect();
    } catch (error) {
      console.error('Sidebar sign-out redirect failed:', error);
    }
  };

  const toggleSidebar = () => {
    const nextCollapsed = !collapsed;
    onToggle();
    if (!nextCollapsed) {
      // Expanding: delay text until transition begins
      setTimeout(() => setShowText(true), 50);
    } else {
      // Collapsing: hide text immediately
      setShowText(false);
    }
  };

  const navItems = useMemo(
    () => [
      { path: '/', icon: 'bi-house-door', label: 'Projects' },
      { path: '/my-workplan', icon: 'bi-calendar-check', label: 'My Workplan' },
      { path: '/charts', icon: 'bi-bar-chart-line', label: 'Charts' },
      { path: '/wbs-manager', icon: 'bi-diagram-3', label: 'WBS Manager' },
      { path: '/configuration', icon: 'bi-gear', label: 'Configuration' }
    ],
    []
  );

  const appLauncherItems = useMemo(
    () => [
      {
        key: 'bdc',
        label: 'BizDev Connect',
        href: 'https://bizdevconnect.nsieng.com/',
        iconImageSrc: '/res/biz_dev_connect_logo.png',
        icon: 'bi-grid',
      },
      {
        key: 'ppc',
        label: 'ProPlan Connect',
        href: '/',
        iconImageSrc: '/res/ppc.png',
        icon: 'bi-kanban',
        current: true,
        disabled: true,
      },
      {
        key: 'pc',
        label: 'Pursuit Connect',
        href: 'https://pursuitconnect.nsieng.com/',
        iconImageSrc: '/res/pc_logo_192.png',
        icon: 'bi-briefcase',
      },
      {
        key: 'riims',
        label: 'RIIMS',
        href: 'https://riimsapp.nsieng.com/',
        iconImageSrc: '/res/nsi-logo-flat-green.png',
        icon: 'bi-graph-up-arrow',
      },
      {
        key: 'ems',
        label: 'Employee Connect',
        href: undefined,
        iconImageSrc: '/res/nsi-logo-flat-white.png',
        icon: 'bi-people',
        disabled: true,
        comingSoon: true,
      },
      {
        key: 'h9',
        label: 'H9 Online',
        href: 'https://h9online.nsieng.com',
        iconImageSrc: '/res/h9_logo.png',
        icon: 'bi-lightning-charge',
      },
    ],
    []
  );

  useEffect(() => {
    const normalizedPath = location.pathname;
    const refToUse = linkRefs.current[normalizedPath];
    if (refToUse) {
      setBarTop(refToUse.offsetTop);
    }
  }, [location.pathname, collapsed, navItems]);

  useEffect(() => {
    const onDocumentMouseDown = (event) => {
      if (!isAppsMenuOpen) return;
      const root = appsMenuRef.current;
      if (!root) return;
      if (root.contains(event.target)) return;
      setIsAppsMenuOpen(false);
    };

    const onDocumentKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsAppsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocumentMouseDown);
    document.addEventListener('keydown', onDocumentKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown);
      document.removeEventListener('keydown', onDocumentKeyDown);
    };
  }, [isAppsMenuOpen]);

  return (
    <div className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="logo-container d-flex align-items-center">
        <Link to="/">
          <img src="/res/nsi-logo-flat-white.png" className="logo-img" alt="Logo" style={{ cursor: 'pointer' }} />
        </Link>
        {!collapsed && <span className="company-name">ProPlanConnect</span>}
        <div className="toggle-btn-container ms-auto" onClick={toggleSidebar} role="button" tabIndex={0}>
          <i className={`bi bi-chevron-left toggle-btn ${collapsed ? 'rotated' : ''}`}></i>
        </div>
      </div>

      <div className="position-relative">
        <div className="active-slide-bar" style={{ top: barTop, width: '4px' }} />
        <ul className="nav flex-column pt-3 main-links">
          {navItems.map(({ path, icon, label }) => {
            const isActive = location.pathname === path;
            return (
              <li key={path} className="nav-item" ref={(el) => (linkRefs.current[path] = el)}>
                <Link to={path} className={`nav-link ${isActive ? 'fw-bold active' : ''}`} aria-label={label}>
                  <i className={`bi ${icon} nav-icon me-2`}></i>
                  {!collapsed && (
                    <span
                      className="link-text"
                      style={{
                        opacity: showText ? 1 : 0,
                        transition: 'opacity 0.3s',
                        pointerEvents: showText ? 'auto' : 'none',
                      }}
                    >
                      {label}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="sidebar-app-launcher" ref={appsMenuRef}>
        <button
          type="button"
          className="sidebar-app-launcher-btn"
          aria-label="Open app launcher"
          aria-expanded={isAppsMenuOpen ? 'true' : 'false'}
          onClick={() => setIsAppsMenuOpen((prev) => !prev)}
        >
          <i className="bi bi-grid-3x3-gap-fill" aria-hidden="true" />
          {!collapsed && showText ? <span className="sidebar-app-launcher-btn-text">Apps</span> : null}
        </button>

        {isAppsMenuOpen ? (
          <div className="sidebar-app-launcher-menu" role="menu" aria-label="App launcher menu">
            <div className="sidebar-app-launcher-grid">
              {appLauncherItems.map((app) => {
                const isDisabled = Boolean(app?.disabled || app?.current);

                const tileBody = (
                  <>
                    {typeof app.iconImageSrc === 'string' && app.iconImageSrc.trim() ? (
                      <img
                        src={app.iconImageSrc}
                        alt=""
                        className="sidebar-app-launcher-item-icon-image"
                        loading="lazy"
                      />
                    ) : (
                      <i className={`bi ${app.icon || 'bi-grid'}`} aria-hidden="true" />
                    )}
                    <span>{app.label}</span>
                    {app?.current ? <em className="sidebar-app-launcher-item-current">Current</em> : null}
                    {app?.comingSoon ? <em className="sidebar-app-launcher-item-coming-soon">Coming Soon</em> : null}
                  </>
                );

                if (isDisabled) {
                  return (
                    <div
                      key={app.key}
                      className="sidebar-app-launcher-item sidebar-app-launcher-item--disabled"
                      role="menuitem"
                      aria-disabled="true"
                    >
                      {tileBody}
                    </div>
                  );
                }

                return (
                  <a
                    key={app.key}
                    href={app.href}
                    target="_blank"
                    rel="noreferrer"
                    className="sidebar-app-launcher-item"
                    role="menuitem"
                    onClick={() => setIsAppsMenuOpen(false)}
                  >
                    {tileBody}
                  </a>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      <div className="user-info-section" style={{ fontSize: collapsed ? '0.8rem' : '1rem' }}>
        {isSignedIn ? (
          <i className="bi bi-person-circle" style={{ fontSize: '2rem' }}></i>
        ) : (
          <i className="bi bi-person-circle" style={{ fontSize: '2rem' }}></i>
        )}

        {collapsed && displayName ? (
          <span className="user-initials">{displayName
            .split(' ')
            .filter(Boolean)
            .map((n) => n[0])
            .join('.')
            .toUpperCase()}.</span>
        ) : null}

        {!collapsed && (
          <div
            className="user-name-display"
            style={{
              opacity: showText ? 1 : 0,
              transition: 'opacity 0.3s',
              pointerEvents: showText ? 'auto' : 'none',
            }}
          >
            <div>{displayName}</div>
            {canShowSignIn && (
              <button type="button" className="sidebar-signin-btn" onClick={handleSignIn}>
                Sign in
              </button>
            )}
            {isSignedIn && (
              <button type="button" className="sidebar-signin-btn" onClick={handleSignOut}>
                Sign out
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}